import React, { useMemo } from 'react';
import {
  Box,
  Breadcrumbs,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CancelIcon from '@mui/icons-material/Cancel';
import SelectionCard from './SelectionCard';

function getSelectionPaths(products) {
  return products
    .map((product) => {
      if (Array.isArray(product.selectionPath) && product.selectionPath.length > 0) {
        return product.selectionPath.map((item) => item.value);
      }
      if (product.productKey) {
        return [product.productKey];
      }
      return null;
    })
    .filter(Boolean);
}

export default function SelectionOverlay({
  open,
  pages,
  products,
  selectionStack,
  onClose,
  onBack,
  onBreadcrumbClick,
  onOptionClick,
  getProductIcon,
}) {
  const breadcrumbItems = useMemo(
    () => [{ label: 'Products', value: 'products' }, ...selectionStack.map((item) => ({ label: item.selected.label, value: item.selected.value }))],
    [selectionStack]
  );

  const selectionPaths = useMemo(() => getSelectionPaths(products), [products]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: 'rgba(0, 0, 0, 0.38)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 1100,
          height: 'calc(100vh - 64px)',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={onBack} disabled={selectionStack.length === 0} aria-label="Back">
              <ArrowBackIosNewIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Choose Product
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a product category, then choose the type to add.
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Cancel selection">
            <CancelIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              return isLast ? (
                <Typography key={`${item.value}-${index}`} color="text.primary" sx={{ fontSize: 13 }}>
                  {item.label}
                </Typography>
              ) : (
                <Typography
                  component="button"
                  key={`${item.value}-${index}`}
                  onClick={() => onBreadcrumbClick(index)}
                  color="text.secondary"
                  sx={{
                    fontSize: 13,
                    background: 'none',
                    border: 'none',
                    p: 0,
                    m: 0,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {item.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </Box>

        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          {pages.length > 0 && (() => {
            const page = pages[pages.length - 1];
            const pageIndex = pages.length - 1;
            const pagePrefix = pages.slice(1, pageIndex + 1).map((p) => p.key);
            const matchesPrefix = (path) => pagePrefix.every((value, index) => path[index] === value);
            return (
              <Box
                key={page.key}
                sx={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', p: 3, overflow: 'hidden' }}
              >
                <Grid container spacing={2} sx={{ width: '100%', maxHeight: 'calc(100vh - 216px)', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' }}>
                  {page.options.map((option) => {
                    const isSelected = selectionPaths.some(
                      (path) => path.length > pageIndex && matchesPrefix(path) && path[pageIndex] === option.value
                    );
                    const isLeafSelected = selectionPaths.some(
                      (path) => path.length === pageIndex + 1 && matchesPrefix(path) && path[pageIndex] === option.value
                    );

                    return (
                      <Grid item xs={12} sm={6} md={4} key={option.value} sx={{ display: 'flex', minWidth: 0 }}>
                        <SelectionCard
                          option={option}
                          isSelected={isSelected}
                          isLeafSelected={isLeafSelected}
                          onClick={() => onOptionClick(option)}
                          onSelectAgain={() => onOptionClick(option)}
                          getProductIcon={getProductIcon}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })()}
        </Box>
      </Paper>
    </Box>
  );
}
